import React from 'react';
import { Prompt } from 'react-router-dom';
import SetPageTitle from 'components/Portals/SetPageTitle';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import Container from 'components/Container';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { Routes, useTypedLocation } from 'routing';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'services/localization';
import LocalizationSettings from './components/LocalizationSettings';
import * as NotificationActions from 'actions/NotificationActions';
import { ShopPublicInfo } from 'common/types';
import { useForm, Controller } from 'react-hook-form';
import SaveButton from 'common/components/SaveButton';
import { api } from 'common/frontend/api';
import TimeZone from './components/TimeZone';
import { setOnboardingActionDone } from 'services/onboarding/utils';

type FormValues = { publicInfo: ShopPublicInfo };

const Localization = () => {
	const { t } = useTranslation();
	const { state } = useTypedLocation();
	const dispatch = useDispatch();
	const shopData = useSelector(ShopSelectors.activeShopData)!;
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const shopAnalytics = useSelector(ShopSelectors.activeShopAnalytics);

	const form = useForm<FormValues>({
		defaultValues: { publicInfo: shopData.publicInfo ?? {} },
	});

	const { isDirty, isSubmitting } = form.formState;

	const onSubmit = async (data: FormValues) => {
		try {
			await api()
				.shops.doc(shopId)
				.public.update({ ...data.publicInfo });
			form.reset(data);
			setOnboardingActionDone('REGIONAL_FORMATS', shopAnalytics);
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
	};

	return (
		<>
			<SetPageTitle
				priority={2}
				title={t('account.regionalFormats.pageTitle', 'Regional Formats')}
				rightContent={
					<SaveButton
						disabled={!isDirty}
						loading={isSubmitting}
						onClick={form.handleSubmit(onSubmit)}
						t={t}
					/>
				}
			/>
			<SetPageNavAction
				priority={2}
				backAction={
					state?.from === Routes.HOME_VIEW
						? { to: Routes.HOME_VIEW, title: t('common:actions.home', 'Home') }
						: { to: Routes.ACCOUNT, title: t('account.pageTitle', 'Account') }
				}
				quickLinks={[]}
			/>
			<Prompt when={isDirty} message={() => t('common:prompts.unsavedChanges')} />
			<Container>
				<Controller
					control={form.control}
					render={(renderProps) => (
						<>
							<LocalizationSettings
								publicInfo={renderProps.field.value}
								setPublicInfo={renderProps.field.onChange}
							/>
							<TimeZone
								publicInfo={renderProps.field.value}
								setPublicInfo={renderProps.field.onChange}
							/>
						</>
					)}
					{...form.register('publicInfo')}
				/>
			</Container>
		</>
	);
};

export default Localization;
